import { useEffect } from 'react';
import { DatePicker, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBalanceTableStore } from 'store/balanceTable';
import { useBalanceTableFiltersStore } from 'store/balanceTableFilters';
import { useMerchantsStore } from 'store/merchants';
import SingleSelect from 'ui/controls/SingleSelect';
import { dateHelpers } from 'utils/helpers/dateHelpers';
import { predicates } from 'utils/helpers/predicates';
import type { TComponent } from 'utils/types/component';

import omitBy from 'lodash/omitBy';

import styles from './Filters.module.scss';
import { filtersHelpers } from './helpers';
import type { IFilterForm } from './types';

export const Filters: TComponent = () => {
    const { t } = useTranslation();

    const [form] = Form.useForm<IFilterForm>();

    const merchantsOptions = useMerchantsStore('merchantNameFilter');
    const initialMerchant = merchantsOptions[0];
    const params = useBalanceTableFiltersStore('params');
    const clearBody = useBalanceTableFiltersStore('clearBody');
    const setBody = useBalanceTableFiltersStore('setBody');
    const setParams = useBalanceTableFiltersStore('setParams');
    const getMany = useBalanceTableStore('getTable');

    const maxDate = dateHelpers.getCurrentDate();

    useEffect(() => {
        return () => {
            form.resetFields();
            clearBody();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (merchantsOptions?.length > 0) {
            const today = dateHelpers.getCurrentDate().startOf('day');

            form.setFieldsValue({
                merchantIdentity: initialMerchant.value,
            });

            const enhanceData = omitBy(
                filtersHelpers.getFiltersData({
                    merchantIdentity: initialMerchant.value,
                    date: today,
                }),
                (field) =>
                    predicates.isUndefined(field) ||
                    predicates.isEmptyString(field) ||
                    predicates.isEmptyArray(field),
            );

            setBody(enhanceData);
            setParams({ ...params, page: 0 });
            getMany(enhanceData, { ...params, page: 0 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchantsOptions]);

    const onSubmit = () => {
        const formValues = form.getFieldsValue();
        const enhanceData = omitBy(
            filtersHelpers.getFiltersData(formValues),
            (field) =>
                predicates.isUndefined(field) ||
                predicates.isEmptyString(field) ||
                predicates.isEmptyArray(field),
        );

        const newParams = { ...params, page: 0 };

        setBody(enhanceData);
        setParams(newParams);

        getMany(enhanceData, newParams);
    };

    const customIcon = <div className={styles.customIcon} />;

    return (
        <Form
            form={form}
            onValuesChange={onSubmit}
            className={styles.leftControls}
        >
            <div className={styles.controlsContainer}>
                <Form.Item name='merchantIdentity'>
                    <SingleSelect
                        placeholder={t('common.form.merchant')}
                        options={merchantsOptions}
                        maxTagCount='responsive'
                        className={styles.multiSelect}
                        customIcon={customIcon}
                    />
                </Form.Item>
                <Form.Item name='date'>
                    <DatePicker
                        placeholder={t('common.form.today')}
                        className={styles.datePicker}
                        maxDate={maxDate}
                    />
                </Form.Item>
            </div>
        </Form>
    );
};
